import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const POLL_MS = 3 * 60 * 1000; // every few minutes

type VersionPayload = { version?: string; builtAt?: string };

async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as VersionPayload;
    const v = data?.version != null ? String(data.version) : null;
    return v || null;
  } catch {
    return null;
  }
}

/**
 * Compares the version.json captured at first load against later polls
 * (route changes + interval). When the deployed build changes, prompts reload.
 */
export function useAppVersionCheck(): { updateAvailable: boolean; reload: () => void } {
  const location = useLocation();
  const baselineRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const check = useCallback(async () => {
    const remote = await fetchVersion();
    if (!remote) return;
    if (baselineRef.current == null) {
      baselineRef.current = remote;
      return;
    }
    if (remote !== baselineRef.current) {
      setUpdateAvailable(true);
    }
  }, []);

  useEffect(() => {
    void check();
  }, [location.pathname, check]);

  useEffect(() => {
    const id = window.setInterval(() => {
      void check();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
}
